/** Persisted when the plugin first loads with ?email=… (etc.) so SPA navigation to checkout still prefill. */
export const PLUGIN_CHECKOUT_PREFILL_STORAGE_KEY =
  'ar_plugin_checkout_prefill_qs';

/** Keys produced by {@link parseCheckoutPrefillFromSearch} from URL/query strings. */
export type CheckoutPrefillParsed = {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  cardholder_first_name?: string;
  cardholder_last_name?: string;
  billing_address_line_1?: string;
  billing_address_line_2?: string;
  billing_city?: string;
  billing_region?: string;
  billing_postal_code?: string;
  billing_country?: string;
};

const decodeQueryParamValuePreservingPlus = (
  search: string,
  key: string
): string => {
  if (!search || !key) return '';
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(?:^|&)${escaped}=([^&]*)`);
  const m = qs.match(re);
  if (!m) return '';
  try {
    return decodeURIComponent(m[1].replace(/\+/g, '%2B')).trim();
  } catch {
    return '';
  }
};

export const parseCheckoutPrefillFromSearch = (
  search: string | null | undefined
): CheckoutPrefillParsed => {
  if (!search || typeof search !== 'string') return {};
  const normalized = search.startsWith('?') ? search : `?${search}`;
  const params = new URLSearchParams(normalized);

  const get = (key: string) => {
    const v = params.get(key);
    return v != null ? v.trim() : '';
  };

  const name = get('name');
  const firstName = get('first_name');
  const lastName = get('last_name');
  const email = get('email');
  const phoneRaw = decodeQueryParamValuePreservingPlus(normalized, 'phone');
  const phone = phoneRaw || get('phone');
  const billingLine1 = get('billing_address_line1');
  const billingLine2 = get('billing_address_line2');
  const billingCity = get('billing_city');
  const billingState = get('billing_state') || get('billing_region');
  const billingPostal =
    get('billing_postal_code') ||
    get('billing_zip_code') ||
    get('zip_code') ||
    get('postal_code') ||
    get('postalCode') ||
    get('postal') ||
    get('postal-code');
  const billingCountry = get('billing_country');

  const prefill: CheckoutPrefillParsed = {};

  if (email) prefill.email = email;
  if (phone) prefill.phone = phone;

  if (firstName) prefill.first_name = firstName;
  if (lastName) prefill.last_name = lastName;
  if (name) {
    prefill.name = name;
    if (!firstName && !lastName) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        prefill.first_name = parts[0];
        prefill.last_name = parts.slice(1).join(' ');
      } else {
        prefill.first_name = name;
        prefill.last_name = '';
      }
    }
    prefill.cardholder_first_name = prefill.first_name;
    prefill.cardholder_last_name = prefill.last_name ?? '';
  } else if (firstName || lastName) {
    prefill.cardholder_first_name = firstName || prefill.first_name;
    prefill.cardholder_last_name = (lastName || prefill.last_name) ?? '';
  }

  if (billingLine1) prefill.billing_address_line_1 = billingLine1;
  if (billingLine2) prefill.billing_address_line_2 = billingLine2;
  if (billingCity) prefill.billing_city = billingCity;
  if (billingState) prefill.billing_region = billingState;
  if (billingPostal) prefill.billing_postal_code = billingPostal;
  if (billingCountry) prefill.billing_country = billingCountry;

  return prefill;
};

/**
 * Call once when the plugins bundle boots (experience list, tour, checkout).
 * Saves checkout prefill query keys from the current URL for later steps.
 */
export const persistPluginCheckoutPrefillFromLocation = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const search = window.location.search;
    if (!search || search.length <= 1) return;
    const prefill = parseCheckoutPrefillFromSearch(search);
    if (Object.keys(prefill).length === 0) return;
    const qs = search.startsWith('?') ? search.slice(1) : search;
    sessionStorage.setItem(PLUGIN_CHECKOUT_PREFILL_STORAGE_KEY, qs);
  } catch (_e) {
    // Quota / private mode — skip
  }
};

export const readPersistedPluginCheckoutPrefillSearch = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const qs = sessionStorage.getItem(PLUGIN_CHECKOUT_PREFILL_STORAGE_KEY);
    if (!qs) return '';
    return qs.startsWith('?') ? qs : `?${qs}`;
  } catch (_e) {
    return '';
  }
};

const MERGE_PREFILL_KEYS = [
  'email',
  'phone',
  'first_name',
  'last_name',
  'name',
  'cardholder_first_name',
  'cardholder_last_name',
  'billing_address_line_1',
  'billing_address_line_2',
  'billing_city',
  'billing_region',
  'billing_country',
] as const satisfies readonly (keyof CheckoutPrefillParsed)[];

export type MergedCheckoutUrlFields = Record<string, unknown> & {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  billing_zip_code?: string;
  zip_code?: string;
  postalCode?: string;
};

export const mergeCheckoutUrlPrefillIntoFields = (
  search: string | null | undefined,
  baseFields: Record<string, unknown> | null | undefined
): MergedCheckoutUrlFields => {
  const base =
    baseFields && typeof baseFields === 'object' ? { ...baseFields } : {};
  const urlPrefill = parseCheckoutPrefillFromSearch(search || '');
  if (Object.keys(urlPrefill).length === 0) {
    return base as MergedCheckoutUrlFields;
  }

  const merged: MergedCheckoutUrlFields = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    ...base,
  };

  MERGE_PREFILL_KEYS.forEach((key) => {
    if (urlPrefill[key] !== undefined) {
      merged[key] = urlPrefill[key];
    }
  });

  if (urlPrefill.billing_postal_code !== undefined) {
    const code = urlPrefill.billing_postal_code;
    merged.billing_zip_code = code;
    merged.zip_code = code;
    merged.postalCode = code;
  }

  return merged;
};

export const mergeCheckoutUrlPrefillFromWindow = (
  baseFields: Record<string, unknown> | null | undefined
): MergedCheckoutUrlFields => {
  if (typeof window === 'undefined')
    return (
      baseFields && typeof baseFields === 'object' ? { ...baseFields } : {}
    ) as MergedCheckoutUrlFields;
  const persisted = readPersistedPluginCheckoutPrefillSearch();
  const live = window.location.search || '';
  const initial =
    baseFields && typeof baseFields === 'object' ? { ...baseFields } : {};
  const afterPersisted = mergeCheckoutUrlPrefillIntoFields(persisted, initial);
  return mergeCheckoutUrlPrefillIntoFields(live, afterPersisted);
};
